import { defineComponent as _defineComponent } from 'vue'
import WidgetDefinition, {
  ParamValues,
} from '@/models/Charts/widgetDefinition';
import ControlledSidebar from '@/components/ControlledSidebar.vue';
import { EventData, ImportantMessageLevel } from '@/models/Charts/chartsData';
import moment, { Moment } from 'moment';
import { isEmpty, isNil } from '@/utils/misc';
import DateGroupingFilter from '../DateGroupingFilter.vue';
import EventTypeFilter from '../EventTypeFilter.vue';
import { FilterTimeAxisSpanEnum } from '@/models/enums/FilterTimeAxisSpanEnum';
import { Logger } from '@/utils/logger';
import DateTimeRangePicker from '@/components/common/dateTimeRangePicker/DateTimeRangePicker.vue';
import { DateTimeGranularity } from '@/components/common/dateTimeRangePicker/DateTimeGranularity';
import EventModuleFilter from '../EventModuleFilter.vue';
import EventDetails from '@/components/careConsole/EventDetails.vue';
import { usersService } from '@/services/users.service';
import { ConsoleEnum } from '@/models/enums/ConsoleEnum';
import WidgetHelp from '@/components/Charts/WidgetHelp.vue';
import { WidgetEnum } from '@/models/enums/WidgetEnum';
import EventCodesFilter from '@/components/EventCodesFilter.vue';
import { UserType } from '@/models/userType';
import { defaultDateRange } from '@/views/careConsole/commons/utils';
import WidgetContainer from '@/components/Charts/widgetContainer/WidgetContainer.vue';
import { SortOrder } from '@/models/enums/SortOrder';
import { computed, defineAsyncComponent, ref, shallowRef } from 'vue';
import { useI18n } from '@/composables/i18n';
// Lazy load to avoid recursion issues (we open this component from EventsTable).

/** @deprecated THIS COMPONENT IS FROZEN **/
const __default__ = {};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'EventsTableExpandedView',
  props: {
    widgetDefinition: null,
    deviceId: null,
    selectedTimeFilter: null,
    routeParams: null,
    dateGrouping: null,
    defaultSortField: { default: 'timestamp' },
    defaultSortOrder: { default: SortOrder.Descending },
    paramValues: { default: () => ({}) },
    module: null,
    timestamp: null
  },
  emits: ['close'],
  setup(__props: any, { emit }) {

const props = __props as { widgetDefinition: WidgetDefinition, deviceId: string, selectedTimeFilter: [string, string], routeParams: Record<string, string>, dateGrouping: FilterTimeAxisSpanEnum, defaultSortField: string, defaultSortOrder: SortOrder, paramValues: ParamValues, module?: string, timestamp?: string };

const EventsTable = defineAsyncComponent(
  () => import('@/components/careConsole/EventsTable.vue'),
);





const { t } = useI18n();

const page = ref(1);

const selectedEvent = shallowRef<EventData | null>(null);

const isOpenedFromChartDoubleClick = computed<boolean>(
  () => !isNil(props.timestamp),
);

const parsedTimestampProp = computed<[Date, Date]>(() => {
  const momentDate = timestampStringToMoment(props.timestamp!);

  if (!momentDate.isValid()) {
    // This should never happen
    Logger.error('Invalid data point timestamp format: ', props.timestamp);
    throw Error(`Invalid data point timestamp format: ${props.timestamp}`);
  }

  const f = (m: moment.Moment) => m.toDate();

  const unitOfTime = FilterTimeAxisSpanEnum[
    props.widgetDefinition!.axisSpan!
  ].toLowerCase() as moment.unitOfTime.StartOf;
  return [f(momentDate.startOf(unitOfTime)), f(momentDate.endOf(unitOfTime))];
});

// We need these three initialized to null instead of to a safe value
// ([] for example). Otherwise, EventsTable.onFilterChanged watcher is
// triggered for each one when mounted. First for the initial value, then for
// the final value set here on mounted.
const dateRangeSelection = ref<[Date, Date]>(getDateRangeSelection());
const selectedDateGrouping = ref<FilterTimeAxisSpanEnum>(props.dateGrouping);
const selectedEventModules = ref<string[]>(getSelectedEventModules());
const selectedEventTypes = ref<ImportantMessageLevel[]>(
  getSelectedEventTypes(),
);
const selectedEventCodes = ref<string[]>(getSelectedEventCodes());
const sortField = ref(props.defaultSortField);
const sortOrder = ref<SortOrder>(props.defaultSortOrder);

function getDateRangeSelection(): [Date, Date] {
  if (isOpenedFromChartDoubleClick.value) {
    return parsedTimestampProp.value;
  } else {
    return [
      moment(props.selectedTimeFilter[0]).toDate(),
      moment(props.selectedTimeFilter[1]).endOf('day').toDate(),
    ];
  }
}

function getSelectedEventModules(): string[] {
  if (props.module) {
    return [props.module];
  }
  if (!isEmpty(props.paramValues.eventModules)) {
    return props.paramValues.eventModules as string[];
  }
  if (!isEmpty(props.widgetDefinition?.paramValues.eventModules)) {
    return props.widgetDefinition?.paramValues.eventModules as string[];
  }
  return [];
}

function getSelectedEventTypes(): ImportantMessageLevel[] {
  if (!isEmpty(props.paramValues.eventTypes)) {
    return props.paramValues.eventTypes as ImportantMessageLevel[];
  }
  if (!isEmpty(props.widgetDefinition?.paramValues.eventTypes)) {
    return props.widgetDefinition?.paramValues
      .eventTypes as ImportantMessageLevel[];
  }
  return [];
}

function getSelectedEventCodes(): string[] {
  if (!isEmpty(props.paramValues.eventCodes)) {
    return props.paramValues.eventCodes as string[];
  }
  if (!isEmpty(props.widgetDefinition?.paramValues.eventCodes)) {
    return props.widgetDefinition?.paramValues.eventCodes as string[];
  }
  return [];
}

function onSort(newSortField: string, newSortOrder: SortOrder) {
  sortField.value = newSortField;
  sortOrder.value = newSortOrder;
}

function timestampStringToMoment(ts: string): Moment {
  return isQuarterDate(ts)
    ? moment(ts, 'YYYY-[Q]Q', true)
    : moment(ts, undefined, true);
}

function isQuarterDate(timestampString: string): boolean {
  return timestampString.toLowerCase().includes('q');
}

function onSidebarClose() {
  selectedEvent.value = null;
}

const dateRangeString = computed<[string, string]>(() => {
  if (isNil(dateRangeSelection.value)) {
    return [
      defaultDateRange()[0].toISOString(),
      defaultDateRange()[1].toISOString(),
    ];
  }

  return [
    dateRangeSelection.value[0].toISOString(),
    dateRangeSelection.value[1].toISOString(),
  ];
});

const eventsTableWidgetDefinition = computed<WidgetDefinition>(() => {
  const customerIdDh = props.routeParams.tenantIdDh
    ? +props.routeParams.tenantIdDh
    : +props.routeParams.customerIdDh;

  return new WidgetDefinition(
    WidgetEnum.EventsTableExpandedView,
    props.deviceId,
    [dateRangeString.value[0], dateRangeString.value[1]],
    [],
    undefined,
    selectedDateGrouping.value,
    undefined,
    {
      customerIdDh,
      modules: selectedEventModules.value,
      eventTypes: selectedEventTypes.value,
      eventCodes: selectedEventCodes.value,
      page: page.value,
      rowsPerPage: 25,
      sortField: sortField.value,
      sortOrder: sortOrder.value,
    },
  )
    .hideTitle()
    .hideInfo();
});

const isSideBarEnabled = computed<boolean>(
  () => currentConsole.value === ConsoleEnum.Care,
);

const currentConsole = computed<ConsoleEnum>(() => {
  const user = usersService.store.current();

  if (user.isServiceUserType) {
    return ConsoleEnum.Care;
  } else if (user.isTechnologyUser) {
    return ConsoleEnum.Technology;
  }

  throw new Error(
    `The user should be eService center or Technology console user: ${
      UserType[user!.userType]
    }`,
  );
});

const dateTimeRangePickerGranularity = computed<DateTimeGranularity>(() => {
  switch (selectedDateGrouping.value) {
    case FilterTimeAxisSpanEnum.None:
      return DateTimeGranularity.SECONDS;
    case FilterTimeAxisSpanEnum.Hour:
      return DateTimeGranularity.HOURS;
    case FilterTimeAxisSpanEnum.Day:
    case FilterTimeAxisSpanEnum.Week:
      return DateTimeGranularity.DAYS;
    case FilterTimeAxisSpanEnum.Month:
    case FilterTimeAxisSpanEnum.Quarter:
    case FilterTimeAxisSpanEnum.Year:
      return DateTimeGranularity.MONTHS;
    default:
      return DateTimeGranularity.SECONDS;
  }
});

return { __sfc: true,EventsTable, props, emit, t, page, selectedEvent, isOpenedFromChartDoubleClick, parsedTimestampProp, dateRangeSelection, selectedDateGrouping, selectedEventModules, selectedEventTypes, selectedEventCodes, sortField, sortOrder, getDateRangeSelection, getSelectedEventModules, getSelectedEventTypes, getSelectedEventCodes, onSort, timestampStringToMoment, isQuarterDate, onSidebarClose, dateRangeString, eventsTableWidgetDefinition, isSideBarEnabled, currentConsole, dateTimeRangePickerGranularity, ControlledSidebar, isNil, DateGroupingFilter, EventTypeFilter, DateTimeRangePicker, EventModuleFilter, EventDetails, WidgetHelp, EventCodesFilter, WidgetContainer }
}

})